import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Checkbox } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
 
import { JobProps, HygienistProps } from '../../../../../utils/types';
import { stringToDate, formatDateTo12HourTimeString, formatDateTo24HourTimeString } from '../../../../../utils/time';
import { 
    generateRateOptions, 
    getDefaultRate,
    colorToCssValue,
    isValidGraduationYear,
    getRateUnlockInfo,
    getAcceptanceFeedback,
    RateOption 
} from '../../../../../utils/rateCalculations';
import moment from 'moment';
import { RateBenchmarkResponse } from '../../../../../api/Api';
import { getTalentMarketBenchmarkRate } from '../../../../../utils/rateGuidance';
import { 
    MINIMUM_PARTIAL_SHIFT_HOURS,
    JOB_SEEKING_STATUS,
    OFFER_RESPONSE_TIME_HOURS,
    TIME_GENERATION
} from '../../../../../utils/constants';
import PlainModal from '../../../../../components/PlainModal';
 
import {
    DialogHeader,
    DialogTitle,
    DialogSubtitle,
    CloseButton,
    OriginalHoursBox,
    OriginalHoursText,
    ContentWrapper,
    Section,
    
    TimeGrid,
    FieldLabel,
    
    CheckboxWrapper,
    CheckboxText,
    ProcessSteps,
    ProcessStep,
    StepNumber,
    StepText,
    CommitmentBox,
    WarningIcon,
    CommitmentContent,
    CommitmentTitle,
    CommitmentText,
    ButtonContainer,
    SubmitButton,
    CancelButton,
    PartialOfferCheckbox,
    PartialOfferCheckboxLabel,
    GHRBonusCallout,
    GHRBonusText,
    SelectedRateDisplay,
    AcceptanceFeedback,
    StatusUnlockBanner,
    StatusUnlockText,
    StatusBadge,
    StatusUpgradeHint,
    CompetingOffersInfo
  } from './OfferModal.styles';
import FixedLoader from '../../../../../components/FixedLoader';
import InlineSelect, { InlineSelectOption } from './InlineSelect';
import modalStyles from './OfferModal.module.scss';
import {
    formatShortNoticeOfferWarning,
    getOfferHoursRemaining,
} from './offerHoursRemaining';

interface OfferSubmitData {
    startTime: string;
    endTime: string;
    hourlyRate: number;
    isPartial: boolean;
    showJobSeekingStatus?: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: OfferSubmitData) => void;
  offerType: 'full' | 'partial';
  job: JobProps;
  hygienist: HygienistProps;
  showJobSeekingStatus: boolean;
  setShowJobSeekingStatus: (val: boolean) => void;
  partialOfferAvailable?: boolean;
  rateBenchmark?: RateBenchmarkResponse | null;
  // Quick-Apply customizations (optional and non-breaking)
  titleOverride?: string;
  subtitleOverride?: string;
  submitTextOverride?: string;
  cancelTextOverride?: string;
  // Submission/loader state (optional)
  submitting?: boolean;
  membershipRateTier?: number;
}

const generateTimeOptions = (
    from: number = TIME_GENERATION.DEFAULT_START_HOUR, 
    to: number = TIME_GENERATION.DEFAULT_END_HOUR, 
    step: number = TIME_GENERATION.DEFAULT_STEP_MINUTES
) => {
    const times = [];
    for (let hour = from; hour <= to; hour++) {
        for (let minute = 0; minute < 60; minute += step) {
            const time = new Date(); time.setHours(hour, minute);
            times.push({
                value: formatDateTo24HourTimeString(time),
                label: formatDateTo12HourTimeString(time),
            });
        }
    }
    return times;
};

/**
 * Generate rate options with personalized coloring based on talent status and graduation year.
 * Uses the new rate calculation system with green/yellow/orange/red zones.
 */
const CalculateShiftRateOptions = (
    hygienist: HygienistProps,
    job: JobProps,
    minimumTopRate?: number
): InlineSelectOption<number>[] => {
    const proposedBase = job.proposedRate ?? job.avgRate ?? job.hourlyRate;

    if (!proposedBase || proposedBase <= 0) {
        return [];
    }

    const rateOptions = generateRateOptions(proposedBase, hygienist, undefined, minimumTopRate);

    // Label font size is 60% of rate font size for optimal mobile/desktop display
    const labelStyle = { 
        fontWeight: 400, 
        fontStyle: 'italic' as const,
        fontSize: '0.6em'
    };

    return rateOptions.map((option: RateOption) => {
        const color = colorToCssValue(option.color);
        
        return {
            value: option.rate,
            ariaLabel: `rate-option-${option.rate}`,
            title: `Rate ${option.rate} per hour`,
            dataTestId: `offer-rate-option-${option.rate}`,
            label: option.isRecommended ? (
                <div style={{ fontWeight: 600, color }}>
                    <b>${option.rate}/hr</b> <span style={labelStyle}>Recommended</span>
                </div>
            ) : option.isHighForShift ? (
                <div style={{ fontWeight: 600, color }}>
                    ${option.rate}/hr <span style={labelStyle}>High for this shift</span>
                </div>
            ) : option.isTooHigh ? (
                <div style={{ fontWeight: 600, color }}>
                    ${option.rate}/hr <span style={labelStyle}>Exceeds shift budget</span>
                </div>
            ) : (
                <span style={{ color, fontWeight: 600 }}>{`$${option.rate}/hr`}</span>
            ),
        };
    });
};

const OfferModal: React.FC<Props> = ({
    open,
    onClose,
    onSubmit,
    offerType,
    job,
    hygienist,
    showJobSeekingStatus,
    setShowJobSeekingStatus,
    partialOfferAvailable = false,
    rateBenchmark,
    titleOverride,
    subtitleOverride,
    submitTextOverride,
    cancelTextOverride,
    submitting = false,
    membershipRateTier,
}) => {
    // Validate graduation year - required for personalized rate calculation
    const hasValidGraduationYear = isValidGraduationYear(hygienist.graduationYear);
    const rateCalculationTalent = useMemo(
        () => ({ ...hygienist, status: membershipRateTier ?? 0 }),
        [hygienist, membershipRateTier]
    );
    
    const shift = job.shifts[0];
    const initialStartTime = formatDateTo24HourTimeString(stringToDate(shift.postedStart, shift.shiftDate));
    const initialEndTime = formatDateTo24HourTimeString(stringToDate(shift.postedEnd, shift.shiftDate));

    // Start the selector at the office proposed rate for this shift.
    const proposedBaseForDefault = job.proposedRate ?? job.avgRate ?? job.hourlyRate;
    const marketBenchmarkTopFloorRate = useMemo(
        () => getTalentMarketBenchmarkRate(rateBenchmark),
        [rateBenchmark]
    );
    const defaultHourlyRate = proposedBaseForDefault 
        ? getDefaultRate(proposedBaseForDefault, rateCalculationTalent, undefined, marketBenchmarkTopFloorRate)
        : 0;
    const [hourlyRate, setHourlyRate] = useState<number>(defaultHourlyRate);
    const lastSyncedDefaultRateRef = useRef(defaultHourlyRate);
    const [startTime, setStartTime] = useState<string>(initialStartTime);
    const [endTime, setEndTime] = useState<string>(initialEndTime);
    const [totalHours, setTotalHours] = useState<number>(0);
    const [estimatedEarnings, setEstimatedEarnings] = useState<number>(0);
    const [isPartialMode, setIsPartialMode] = useState<boolean>(false);

    const ghrBonus = (() => {
        if (job?.ghrBonus?.rate) {
            return job?.ghrBonus?.rate;
        }
        if (job?.ghrBonus?.tier === 1) {
            return (job?.ghrBonus?.windowMeta?.tier1RateCents / 100);
        }
        if (job?.ghrBonus?.tier === 2) {
            return (job?.ghrBonus?.windowMeta?.tier2RateCents / 100);
        }
    })()

    const rateOptions = useMemo(
        () => CalculateShiftRateOptions(rateCalculationTalent, job, marketBenchmarkTopFloorRate),
        [rateCalculationTalent, job, marketBenchmarkTopFloorRate]
    );

    const unlockInfo = useMemo(() =>
        proposedBaseForDefault
            ? getRateUnlockInfo(proposedBaseForDefault, rateCalculationTalent, undefined, marketBenchmarkTopFloorRate)
            : null,
        [proposedBaseForDefault, rateCalculationTalent, marketBenchmarkTopFloorRate]
    );

    const acceptanceFeedback = useMemo(() => {
        if (!hourlyRate || !proposedBaseForDefault) return null;
        return getAcceptanceFeedback(hourlyRate, proposedBaseForDefault, rateCalculationTalent, undefined, marketBenchmarkTopFloorRate);
    }, [hourlyRate, proposedBaseForDefault, rateCalculationTalent, marketBenchmarkTopFloorRate]);

    const shiftStart: Date = stringToDate(shift.postedStart, shift.shiftDate);
    const shiftEnd: Date = stringToDate(shift.postedEnd, shift.shiftDate);

    const timeOptions = useMemo(() => {
        const hoursToMs = (hours: number) => hours * 60 * 60 * 1000;
        const latestStart = new Date(shiftEnd.getTime() - hoursToMs(MINIMUM_PARTIAL_SHIFT_HOURS));
        
        const allOptions = generateTimeOptions(shiftStart.getHours(), shiftEnd.getHours());
        
        const validOptions = allOptions.filter(option => {
            const optionTime: Date = stringToDate(option.value, shift.shiftDate);
            return optionTime.getTime() >= shiftStart.getTime() && optionTime.getTime() <= latestStart.getTime();
        });
        
        // Add explanatory disabled option if there are invalid times
        const hasInvalidOptions = allOptions.some(option => {
            const optionTime: Date = stringToDate(option.value, shift.shiftDate);
            return optionTime.getTime() > latestStart.getTime() && optionTime.getTime() < shiftEnd.getTime();
        });
        
        if (hasInvalidOptions && validOptions.length > 0) {
            return [
                ...validOptions,
                { value: 'minimum-message', label: '── Minimum 3hrs required ──' }
            ];
        }
        
        return validOptions;
    }, [shiftStart, shiftEnd, shift.shiftDate]);

    const endTimeOptions = useMemo(() => {
        if (!startTime) return [];
        const startDateTime: Date = stringToDate(startTime, shift.shiftDate);

        const hoursToMs = (hours: number) => hours * 60 * 60 * 1000;
        const earliestEnd = new Date(startDateTime.getTime() + hoursToMs(MINIMUM_PARTIAL_SHIFT_HOURS));
        
        const allOptions = generateTimeOptions(shiftStart.getHours(), shiftEnd.getHours(), TIME_GENERATION.DEFAULT_STEP_MINUTES);
        const validOptions = allOptions.filter(option => {
            const optionTime: Date = stringToDate(option.value, shift.shiftDate);
            return optionTime.getTime() >= earliestEnd.getTime() && optionTime.getTime() <= shiftEnd.getTime();
        });
        
        // Add explanatory disabled option if there are invalid times
        const hasInvalidOptions = allOptions.some(option => {
            const optionTime: Date = stringToDate(option.value, shift.shiftDate);
            return optionTime.getTime() > startDateTime.getTime() && optionTime.getTime() < earliestEnd.getTime();
        });
        
        if (hasInvalidOptions && validOptions.length > 0) {
            return [
                { value: 'minimum-message', label: '── Minimum 3hrs required ──' },
                ...validOptions
            ];
        }
        
        return validOptions;
    }, [startTime, shift.shiftDate, shiftEnd, shiftStart]);

    useEffect(() => {
        if (!open) {
            lastSyncedDefaultRateRef.current = defaultHourlyRate;
            return;
        }

        const shouldSyncHourlyRate = hourlyRate === lastSyncedDefaultRateRef.current || hourlyRate === 0;
        if (shouldSyncHourlyRate && hourlyRate !== defaultHourlyRate) {
            setHourlyRate(defaultHourlyRate);
        }

        lastSyncedDefaultRateRef.current = defaultHourlyRate;
    }, [defaultHourlyRate, hourlyRate, open]);

    // Reset partial mode when modal closes and lock background scroll while open
    useEffect(() => {
        const root = document.getElementById('root');
        const cls = 'no-vertical-scroll';
        if (open) {
            document.body.classList.add(cls);
            document.documentElement.classList.add(cls);
            root && root.classList.add(cls);
        } else {
            setIsPartialMode(false);
            document.body.classList.remove(cls);
            document.documentElement.classList.remove(cls);
            root && root.classList.remove(cls);
        }
        return () => {
            document.body.classList.remove(cls);
            document.documentElement.classList.remove(cls);
            root && root.classList.remove(cls);
        };
    }, [open]);

    useEffect(() => {
        const start: Date = stringToDate(startTime, job.shifts[0].shiftDate);
        const end: Date = stringToDate(endTime, job.shifts[0].shiftDate);
        
        let hours = 0;
        if (!isPartialMode) {
          const fullStart: Date = stringToDate(shift.postedStart, shift.shiftDate);
          const fullEnd: Date = stringToDate(shift.postedEnd, shift.shiftDate);
          const diffMs = fullEnd.getTime() - fullStart.getTime();
          hours = diffMs / (1000 * 60 * 60);
        } else if (end.getTime() > start.getTime()) {
          const diffMs = end.getTime() - start.getTime();
          hours = diffMs / (1000 * 60 * 60);
        }
  
        setTotalHours(hours);
        setEstimatedEarnings(hours * hourlyRate);
      }, [startTime, endTime, hourlyRate, isPartialMode, job, shift]);

    const handleSubmit = () => {
        onSubmit({
            hourlyRate: hourlyRate,
            startTime: !isPartialMode ? shift.postedStart : startTime,
            endTime: !isPartialMode ? shift.postedEnd : endTime,
            isPartial: isPartialMode,
            showJobSeekingStatus: showJobSeekingStatus
        });
    };

    const isPartialShiftInvalid = isPartialMode && totalHours < MINIMUM_PARTIAL_SHIFT_HOURS;

    // Full Shift Offer only — short-notice warning uses fixed posted start (Partial out of scope).
    // Prefer job.timeZone (location IANA); getOfferHoursRemaining falls back to browser local if unset.
    const shortNoticeHoursRemaining = useMemo(() => {
        if (isPartialMode) {
            return null;
        }
        return getOfferHoursRemaining({
            shiftDate: shift.shiftDate || job.startDate,
            postedStart: shift.postedStart,
            timeZone: job.timeZone,
        });
    }, [isPartialMode, shift.shiftDate, shift.postedStart, job.startDate, job.timeZone]);

    const shortNoticeWarningText = shortNoticeHoursRemaining != null
        ? formatShortNoticeOfferWarning(shortNoticeHoursRemaining)
        : null;

    // Error state for invalid graduation year
    if (!hasValidGraduationYear) {
        return (
            <PlainModal
                isOpen={open}
                onClose={onClose}
                size="sm"
                ariaLabel="Unable to Submit Offer"
                dataTestId="offer-error-modal"
                rootClassName={modalStyles.root}
                dialogClassName={modalStyles.dialog}
            >
                <DialogHeader>
                    <div>
                        <DialogTitle>Unable to Submit Offer</DialogTitle>
                        <DialogSubtitle>Profile information required</DialogSubtitle>
                    </div>
                    <CloseButton
                        onClick={onClose}
                        className="no-fastclick"
                        aria-label="close-error-modal"
                        data-testid="error-modal-close"
                    >
                        <CloseIcon />
                    </CloseButton>
                </DialogHeader>
                <ContentWrapper>
                    <Section>
                        <CommitmentBox 
                            severity="warning"
                            icon={
                                <WarningIcon>
                                    <WarningAmberIcon />
                                </WarningIcon>
                            }
                        >
                            <CommitmentContent>
                                <CommitmentTitle>Profile Update Required</CommitmentTitle>
                                <CommitmentText>
                                    There was a problem with your profile information. Please contact support for help at <strong>help@tempstars.com</strong>
                                </CommitmentText>
                            </CommitmentContent>
                        </CommitmentBox>
                    </Section>
                    <ButtonContainer>
                        <CancelButton
                            variant="outlined"
                            onClick={onClose}
                            fullWidth
                            className="no-fastclick"
                            aria-label="close-error"
                            data-testid="error-close-button"
                        >
                            Close
                        </CancelButton>
                    </ButtonContainer>
                </ContentWrapper>
            </PlainModal>
        );
    }

    return (
        <PlainModal
            isOpen={open}
            onClose={onClose}
            size="sm"
            ariaLabel="offer-modal"
            contentDesc="offer-modal"
            dataTestId="offer-modal"
            rootClassName={modalStyles.root}
            dialogClassName={modalStyles.dialog}
            disableBackdropClose={submitting}
            disableEscapeKeyDown={submitting}
        >
            <DialogHeader>
                <div>
                    <DialogTitle>{titleOverride || (`${isPartialMode ? 'Partial' : 'Full'} Shift Offer`)}</DialogTitle>
                    <DialogSubtitle>{subtitleOverride || 'Select your rate and confirm availability'}</DialogSubtitle>
                </div>
                <CloseButton
                    onClick={() => { if (submitting) return; onClose(); }}
                    className="no-fastclick"
                    aria-label="close-offer-modal"
                    data-testid="offer-modal-close"
                >
                    <CloseIcon />
                </CloseButton>
            </DialogHeader>
            <ContentWrapper>
                <OriginalHoursBox>
                    <AccessTimeIcon />
                    <OriginalHoursText>
                        {`Shift Date: ${moment(job.startDate).format('dddd MMMM D')}
Hours: ${job.shifts[0].postedStart} - ${job.shifts[0].postedEnd}`}
                    </OriginalHoursText>
                </OriginalHoursBox>

                {partialOfferAvailable && (
                    <PartialOfferCheckbox
                        onClick={() => setIsPartialMode(!isPartialMode)}
                        className="no-fastclick"
                        aria-label="toggle-partial-offer"
                        data-testid="offer-partial-checkbox"
                    >
                        <Checkbox
                            checked={isPartialMode}
                            onChange={(e) => setIsPartialMode(e.target.checked)}
                            size="small"
                            sx={{ padding: 0 }}
                            className="no-fastclick"
                        />
                        <PartialOfferCheckboxLabel>
                            {`Can't work the entire shift?
Submit a Partial Offer to work part of the shift`}
                        </PartialOfferCheckboxLabel>
                    </PartialOfferCheckbox>
                )}

                {isPartialMode && (
                    <Section>
                        <TimeGrid>
                            <div>
                                <FieldLabel>Start Time</FieldLabel>
                                <InlineSelect
                                    value={startTime}
                                    onChange={setStartTime}
                                    options={timeOptions.map(option => ({
                                        value: option.value,
                                        label: option.label,
                                        disabled: option.value === 'minimum-message',
                                        ariaLabel: `start-time-option-${option.value}`,
                                        title: `Start time ${option.label}`,
                                        dataTestId: `offer-start-time-option-${option.value}`,
                                    }))}
                                    className="no-fastclick"
                                    ariaLabel="start-time-select"
                                    id="offer-start-time-select"
                                    dataTestId="offer-start-time-select"
                                />
                            </div>
                            <div>
                                <FieldLabel>End Time</FieldLabel>
                                <InlineSelect
                                    value={endTime}
                                    onChange={setEndTime}
                                    options={endTimeOptions.map(option => ({
                                        value: option.value,
                                        label: option.label,
                                        disabled: option.value === 'minimum-message',
                                        ariaLabel: `end-time-option-${option.value}`,
                                        title: `End time ${option.label}`,
                                        dataTestId: `offer-end-time-option-${option.value}`,
                                    }))}
                                    className="no-fastclick"
                                    ariaLabel="end-time-select"
                                    id="offer-end-time-select"
                                    dataTestId="offer-end-time-select"
                                />
                            </div>
                        </TimeGrid>
                    </Section>
                )}

                <Section>
                    <FieldLabel>Select Your Hourly Rate</FieldLabel>
                    <InlineSelect
                        value={hourlyRate}
                        onChange={(value) => setHourlyRate(Number(value))}
                        options={rateOptions}
                        className="no-fastclick"
                        ariaLabel="rate-select"
                        id="offer-rate-select"
                        dataTestId="offer-rate-select"
                    />

                    {acceptanceFeedback && (
                        <AcceptanceFeedback $feedbackColor={acceptanceFeedback.cssColor}>
                            <InfoOutlinedIcon />
                            <span>{acceptanceFeedback.message}</span>
                        </AcceptanceFeedback>
                    )}

                    {unlockInfo?.hasUnlock && (
                        <StatusUnlockBanner>
                            <LockOpenIcon />
                            <StatusUnlockText>
                                Your <StatusBadge $variant={unlockInfo.statusLabel === 'Elite' ? 'elite' : 'pro'}>{unlockInfo.statusLabel}</StatusBadge> status
                                unlocks rates up to <strong>${unlockInfo.topRate}/hr</strong>
                            </StatusUnlockText>
                        </StatusUnlockBanner>
                    )}

                    {unlockInfo && !unlockInfo.hasUnlock && (rateCalculationTalent.status ?? 0) < 1 && (
                        <StatusUpgradeHint>
                            Earn <StatusBadge $variant="pro">Pro</StatusBadge> or <StatusBadge $variant="elite">Elite</StatusBadge> status to unlock higher rates.
                            <a href="https://support.tempstars.com/hc/en-us/articles/membership-tiers"
                               target="_system" className="external">
                                Learn more
                            </a>
                        </StatusUpgradeHint>
                    )}

                    {job.offerStats && (
                        <CompetingOffersInfo>
                            <PeopleOutlineIcon />
                            {job.offerStats.offerCount > 0 ? (
                                <span>
                                    This office already has some offers for this shift. Staying close to the office's proposed rate usually gives you the best chance of being accepted.
                                </span>
                            ) : (
                                <span>No offers for this shift yet — submit yours to be the first!</span>
                            )}
                        </CompetingOffersInfo>
                    )}

                    {job.ghrBonus && (
                        <>
                            <GHRBonusCallout>
                                <CardGiftcardIcon />
                                <GHRBonusText>
                                    Bonus: Earn <span className="amount">+${ghrBonus}/hr</span> extra until <span className="date">{
                                    new Date(
                                        (job.ghrBonus.tier === 2 && job.ghrBonus.tier2EndDate)
                                            ? job.ghrBonus.tier2EndDate 
                                            : job.ghrBonus.endDate)
                                        .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                }</span>
                                </GHRBonusText>
                            </GHRBonusCallout>
                            
                            {hourlyRate && (
                                <SelectedRateDisplay>
                                    <div className="label">Your selected rate with bonus:</div>
                                    <div>
                                        <span className="rate">${hourlyRate}</span>
                                        <span className="bonus">+ ${ghrBonus}/hr</span>
                                    </div>
                                </SelectedRateDisplay>
                            )}
                        </>
                    )}
                </Section>

                {hygienist?.showForHire === JOB_SEEKING_STATUS.ACTIVELY_LOOKING && (
                    <Section>
                        <CheckboxWrapper>
                            <Checkbox
                                checked={showJobSeekingStatus}
                                onChange={(e) => setShowJobSeekingStatus(e.target.checked)}
                                size="small"
                                sx={{ padding: 0 }}
                                className="no-fastclick"
                            />
                            <CheckboxText>
                                Let this office know your Job-Seeking Status: <span className="status">Actively looking for a position</span>
                            </CheckboxText>
                        </CheckboxWrapper>
                    </Section>
                )}

                <Section>
                    <ProcessSteps>
                        <ProcessStep>
                            <StepNumber>1</StepNumber>
                            <StepText>
                                We'll send your Offer immediately to office. <strong>Do not call them directly</strong>
                            </StepText>
                        </ProcessStep>
                        <ProcessStep>
                            <StepNumber>2</StepNumber>
                            <StepText>
                                The office has up to {OFFER_RESPONSE_TIME_HOURS} hours to review and respond before your offer expires
                            </StepText>
                        </ProcessStep>
                        <ProcessStep>
                            <StepNumber>3</StepNumber>
                            <StepText>
                                If your Offer is Accepted, you'll be notified via Email & Text Message that you are Booked & Confirmed for this shift.
                            </StepText>
                        </ProcessStep>
                    </ProcessSteps>
                </Section>

                <Section>
                    <CommitmentBox 
                        severity="warning"
                        icon={
                            <WarningIcon>
                                <WarningAmberIcon />
                            </WarningIcon>
                        }
                    >
                        <CommitmentContent>
                            <CommitmentTitle>Professional Commitment</CommitmentTitle>
                            <CommitmentText>
                                {shortNoticeWarningText && (
                                    <>
                                        <span data-testid="offer-short-notice-warning">
                                            {shortNoticeWarningText}
                                        </span>
                                        <br /><br />
                                    </>
                                )}
                                If your offer is accepted, you have an important obligation to the office and their patients.
                                <br /><br />
                                TempStars members are reliable and keep their shift commitments. <strong>Cancelling a shift affects your ability to use TempStars.</strong>
                            </CommitmentText>
                        </CommitmentContent>
                    </CommitmentBox>
                </Section>



                <ButtonContainer>
                    <SubmitButton
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isPartialShiftInvalid || submitting}
                        fullWidth
                        startIcon={<CheckCircleIcon />}
                        className="no-fastclick"
                        aria-label="submit-offer"
                        data-testid="offer-submit-button"
                    >
                        {submitTextOverride || `Submit ${isPartialMode ? 'Partial' : 'Full'} Shift Offer`}
                    </SubmitButton>
                    <CancelButton
                        variant="outlined"
                        onClick={onClose}
                        disabled={submitting}
                        fullWidth
                        className="no-fastclick"
                        aria-label="cancel-offer"
                        data-testid="offer-cancel-button"
                    >
                        {cancelTextOverride || 'Cancel'}
                    </CancelButton>
                </ButtonContainer>
            </ContentWrapper>
            <FixedLoader loading={Boolean(submitting)} />
        </PlainModal>
    );
};

export default OfferModal;
